import {
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import styled from 'styled-components';
import { FlexWrap, Icon, Input, Typography } from '@repo/components';
import { ICON_WARNING } from '@repo/assets';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { JPN_BANK_LIST, JPN_CITY_LIST } from '@repo/constants';
import { attachedHyphensPostCode } from '@repo/utils';
import { AccountInfo } from '@repo/types';
import { useFindKeyword } from '@repo/hooks';

type Props = {
  register: UseFormRegister<AccountInfo>;
  getValues: UseFormGetValues<AccountInfo>;
  setValue: UseFormSetValue<AccountInfo>;
  errors?: FieldErrors;
  disabled?: boolean;
  selectedAccountType: string;
  setSelectedAccountType: Dispatch<SetStateAction<string>>;
};

export const InfoJPN = ({
  errors,
  register,
  getValues,
  setValue,
  disabled,
  selectedAccountType,
  setSelectedAccountType,
}: Props) => {
  const [isDropList, setIsDropList] = useState(false);
  const [keywordList, setKeyWordList] = useState<string[]>([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const [isJpnCityFocus, setIsJpnCityFocus] = useState(false);
  const { searchList, keyword, setKeyword } = useFindKeyword();
  let dropDownRef = useRef<any>(null);

  // 드롭박스 스크롤 관련코드
  // useEffect(() => {
  //   if (isDropList && dropDownRef.current && selectedItemIndex !== -1) {
  //     const item = dropDownRef.current.children[selectedItemIndex];
  //     if (item) {
  //       item.scrollIntoView({ behavior: 'smooth', block: 'center' });
  //     }
  //   }
  // }, [isDropList, selectedItemIndex]);

  // 일본 은행 검색 기능
  const bankObject: { [key: string]: string } = JPN_BANK_LIST.reduce(
    (acc, bank) => {
      acc[bank.toUpperCase()] = bank;
      return acc;
    },
    {} as { [key: string]: string },
  );

  const onChangeAndSearchKeyword = (e: any) => {
    const value = e.target.value;
    if (value === '') {
      setSelectedItemIndex(-1);
      setIsDropList(false);
    }
    const newBankList = Object.keys(bankObject)
      .filter((key) => {
        const sanitizedKey = key
          .replace(/[^a-zA-Z0-9ぁ-んァ-ン一-龯]/g, '')
          .toUpperCase();
        return (
          sanitizedKey.includes(
            value.replace(/[^a-zA-Z0-9ぁ-んァ-ン一-龯]/g, '').toUpperCase(),
          ) && sanitizedKey !== ''
        );
      })
      .map((key) => bankObject[key]);
    if (newBankList.length > 0 && value) {
      setIsDropList(true);
    } else {
      setIsDropList(false);
    }

    setKeyWordList(newBankList);
  };

  const sanitizeInput = (value: string) => {
    return value.replace(/[^a-zA-Z0-9, ]/g, '');
  };

  return (
    <>
      <InputWrapper>
        <Input
          {...register('bank', {
            required: 'This is a required item.',
            onChange: onChangeAndSearchKeyword,
          })}
          label='Bank account'
          placeholder='Name of Bank'
          autoComplete='none'
          type='text'
          error={errors?.bank}
          disabled={disabled}
          isRequired
          onBlur={async () => {
            let isSelected = false;
            setIsDropList(false);
            const bank = getValues('bank')?.toUpperCase();

            Object.keys(bankObject).forEach((key) => {
              if (key === bank) {
                isSelected = true;
              }
            });
            if (!isSelected) {
              setValue('bank', '');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSelectedItemIndex((prev) =>
                Math.min(prev + 1, keywordList.length - 1),
              );
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSelectedItemIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setIsDropList(false);
              setSelectedItemIndex(-1);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              setValue('bank', keywordList[selectedItemIndex]);
              setIsDropList(false);
            }
          }}
        />
        {isDropList && (
          <DropList ref={dropDownRef}>
            {keywordList.map((bank, idx) => (
              <DropItem
                key={idx}
                isSelected={selectedItemIndex === idx}
                onMouseDown={() => {
                  setValue('bank', bank);
                }}
              >
                {bank}
              </DropItem>
            ))}
          </DropList>
        )}
      </InputWrapper>
      <Input
        {...register('bankAccount', {
          required: 'This is a required item.',
          pattern: {
            value: /^\d{7}$/,
            message: 'Please enter the number 7 digits.',
          },
          onChange: (e) =>
            setValue('bankAccount', sanitizeInput(e.target.value)),
        })}
        type='text'
        placeholder='Bank Account Number (口座番号+)'
        maxLength={7}
        error={errors?.bankAccount}
        margin={[8, 0, 0]}
        disabled={disabled}
      />
      <Typography
        size='12px'
        lineHeight='20px'
        fontWeight='400'
        color='#A7A7A7'
        margin='8px 0 0 0'
      >
        * 法人の銀行口座は受け付けておりません。
      </Typography>
      <Input
        {...register('branchInformation', {
          required: 'This is a required item.',
          pattern: {
            value: /^\d{3}$/,
            message: 'Please enter only numbers (3-digits)',
          },
        })}
        label='支店番号'
        isRequired
        placeholder='Branch Number (3-digits)'
        margin={[20, 0, 0]}
        disabled={disabled}
        maxLength={3}
        error={errors?.branchInformation}
      />
      <JpnCityWrapper>
        <Input
          {...register('stateProvinceRegion', {
            required: 'This is a required item.',
            pattern: {
              value: /^[a-zA-Z][a-zA-Z\s]*$/,
              message: 'Please enter only English',
            },
            onChange: (e) => setKeyword(e.target.value),
            validate: (value) => {
              const isSelected = JPN_CITY_LIST.find((city) => city === value);
              return !!isSelected || 'Please select 都道府県 correctly';
            },
          })}
          type='text'
          label='都道府県(英語)'
          isRequired
          placeholder='Prefecture (英語のみ English only)'
          autoComplete='none'
          disabled={disabled}
          error={errors?.stateProvinceRegion}
          onBlur={() => setIsJpnCityFocus(false)}
          onFocus={() => setIsJpnCityFocus(true)}
        />
        {isJpnCityFocus &&
          searchList.length !== 0 &&
          searchList[0] !== keyword && (
            <DropList>
              {searchList.map((city, index) => {
                return (
                  <DropItem
                    key={index}
                    isSelected={false}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setValue('stateProvinceRegion', city, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      setKeyword(city);
                    }}
                  >
                    {city}
                  </DropItem>
                );
              })}
            </DropList>
          )}
      </JpnCityWrapper>
      <Input
        {...register('city', {
          required: 'This is a required item.',
          pattern: {
            value: /^[a-zA-Z][a-zA-Z\s]*$/,
            message: 'Please enter only English',
          },
          onChange: (e) => setValue('city', sanitizeInput(e.target.value)),
        })}
        label='市(英語)'
        isRequired
        placeholder='City (英語のみ English only)'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.city}
      />
      <Input
        {...register('address', {
          required: 'This is a required item.',
          pattern: {
            value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
            message: 'Please enter only English and numbers.',
          },
        })}
        label='注所 1 (英語)'
        isRequired
        placeholder='Address (英語のみ English and numbers only)'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.address}
      />
      <Input
        {...register('addressDetail2', {
          required: 'This is a required item.',
          pattern: {
            value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
            message: 'Please enter only English and numbers.',
          },
        })}
        label='注所 2 | アパートの名前、番地(英語)'
        isRequired
        placeholder='APT Name, Number (英語のみ English and numbers only)'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.addressDetail2}
      />
      <Input
        {...register('zipPostalCode', {
          required: 'This is a required item.',
          pattern: {
            value: /^[0-9-]{8}$/,
            message: 'Please enter the number 7 digits.',
          },
        })}
        label='郵便番号'
        isRequired
        value={attachedHyphensPostCode(getValues('zipPostalCode') || '')}
        maxLength={8}
        placeholder='Post Code (numbers only)'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.zipPostalCode}
      />
      <LabelWrapper marginTop='20px'>
        <Label>口座タイプ</Label>
        <RequiredDot>*</RequiredDot>
      </LabelWrapper>
      <FlexWrap dir='column' justifyContent='center' gap='16px'>
        <RadioLabel>
          <input
            {...register('accountType', {
              required: 'This is a required item.',
            })}
            type='radio'
            value='普通 (Futsuu) current/general'
            onClick={() =>
              setSelectedAccountType('普通 (Futsuu) current/general')
            }
            checked={selectedAccountType === '普通 (Futsuu) current/general'}
            disabled={disabled}
          />
          普通 (Futsuu) current/general
        </RadioLabel>
        <RadioLabel>
          <input
            {...register('accountType', {
              required: 'This is a required item.',
            })}
            type='radio'
            value='当座 (Touza) checking'
            onClick={() => setSelectedAccountType('当座 (Touza) checking')}
            checked={selectedAccountType === '当座 (Touza) checking'}
            disabled={disabled}
          />
          当座 (Touza) checking
        </RadioLabel>
        {errors?.accountType && (
          <FlexWrap gap='8px'>
            <Icon imgUrl={ICON_WARNING} width='18px' height='18px' />
            <Typography color='#f13e4b' size='13px' fontWeight='400'>
              This is a required item.
            </Typography>
          </FlexWrap>
        )}
        <Typography
          size='12px'
          lineHeight='150%'
          fontWeight='400'
          color='#A7A7A7'
        >
          *貯蓄口座の払い戻しを受けることはできません。
        </Typography>
      </FlexWrap>
    </>
  );
};

const InputWrapper = styled.div`
  position: relative;
`;

const LabelWrapper = styled.div<{ marginTop?: string }>`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-top: ${(props) => props.marginTop && props.marginTop};
`;

const Label = styled.label`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  @media ${(props) => props.theme.mobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;
const RequiredDot = styled.div`
  color: #f13e4b;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  margin-left: 4px;
  padding-bottom: 6px;
`;

const RadioLabel = styled.label`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #212121;
`;

const JpnCityWrapper = styled(InputWrapper)`
  margin-top: 20px;
`;

const DropList = styled.ul`
  position: absolute;
  max-height: 300px;
  overflow: auto;
  z-index: 10;
  top: 100px;
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`;

const DropItem = styled.li<{ isSelected: boolean }>`
  font-size: 12px;
  padding: 10px 6px;
  background-color: ${(props) => props.isSelected && '#d3e2fd'};
  :hover {
    background-color: #d3e2fd;
    cursor: pointer;
  }
`;
